import React, { useEffect, useState, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, getDocs, where, doc, getDoc } from '../firebaseConfig';
import { Scatter } from 'react-chartjs-2';
import Card from '@mui/material/Card';
import MDBox from '../components/MDBox';
import Divider from '@mui/material/Divider';
import TimelineIcon from '@mui/icons-material/Timeline';
import MDTypography from '../components/MDTypography';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { usePeriod } from '../components/PeriodContext';

const TeamRRRatioAnalysisChart = ({ userID, tradeAccountID = 'default' }) => {
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag } = usePeriod();
  const [trades, setTrades] = useState([]);
  const [accountName, setAccountName] = useState(tradeAccountID);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  // Fetch account name
  useEffect(() => {
    const fetchAccountName = async () => {
      if (tradeAccountID === 'default') {
        setAccountName('Default');
        return;
      }
      try {
        const accountDoc = await getDoc(doc(db, 'Users', userID, 'tradeAccounts', tradeAccountID));
        if (accountDoc.exists()) {
          setAccountName(accountDoc.data().name || tradeAccountID);
        }
      } catch (error) {
        console.error('Error fetching account name:', error);
      }
    };
    fetchAccountName();
  }, [userID, tradeAccountID]);

  // Fetch trades
  useEffect(() => {
    const fetchTrades = async () => {
      setLoading(true);
      try {
        const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const tradesQuery = query(collection(db, tradesPath));
        const tradeSnapshot = await getDocs(tradesQuery);
        const tradesData = tradeSnapshot.docs.map((doc) => ({
          id: doc.id,
          accountID: tradeAccountID,
          ...doc.data(),
          date: doc.data().date instanceof Object ? doc.data().date.toDate() : new Date(doc.data().date),
          exitDate: doc.data().exitDate instanceof Object ? doc.data().exitDate.toDate() : new Date(doc.data().exitDate),
        }));
        setTrades(tradesData);
        setHasData(tradesData.length > 0);
        console.log('Fetched trades for userID:', userID, 'tradeAccountID:', tradeAccountID, 'Count:', tradesData.length);
      } catch (error) {
        console.error('Error fetching trades for TeamRRRatioAnalysisChart:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTrades();
  }, [userID, tradeAccountID]);

  const filterEntries = (entries) => {
    const matchesPeriod = (entryDate) => {
      const date = new Date(entryDate);
      if (selectedPeriod === 'today') {
        return date.toDateString() === new Date().toDateString();
      } else if (selectedPeriod === 'this-week') {
        const getWeek = (d) => {
          const date = new Date(d.getTime());
          date.setHours(0, 0, 0, 0);
          date.setDate(date.getDate() + 4 - (date.getDay() || 7));
          const yearStart = new Date(date.getFullYear(), 0, 1);
          return Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
        };
        return getWeek(date) === getWeek(new Date()) && date.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'this-month') {
        return date.getMonth() === new Date().getMonth() && date.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'this-year') {
        return date.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'pick-month') {
        return date.getMonth() + 1 === selectedMonth && date.getFullYear() === selectedYear;
      } else if (selectedPeriod === 'pick-year') {
        return date.getFullYear() === selectedYear;
      } else if (selectedPeriod === 'pick-month-year') {
        return date.getMonth() + 1 === selectedMonth && date.getFullYear() === selectedYear;
      } else {
        return true; // all-time
      }
    };

    return entries.filter((entry) => {
      const matchesTag = selectedTag === 'all' || (Array.isArray(entry.tags) && entry.tags.includes(selectedTag));
      return matchesPeriod(entry.exitDate) && entry.status === (showMissedTrades ? 'Missed' : 'Closed') && matchesTag;
    });
  };

  const calculateRRData = useMemo(() => {
    const filteredTrades = filterEntries(trades);
    const rrData = filteredTrades
      .map((trade) => {
        const risk = trade.entry - trade.sl;
        const reward = trade.tp - trade.entry;
        const rrRatio = risk !== 0 ? reward / risk : 0;
        const outcome = trade.returns > 0 ? 'Win' : 'Loss';
        const returns = parseFloat(trade.returns || 0);

        if (rrRatio < -100 || rrRatio > 100 || returns < -10000 || returns > 10000) return null;

        return { x: returns, y: rrRatio, outcome };
      })
      .filter((trade) => trade !== null);

    console.log('Calculated RR data:', rrData);
    return rrData;
  }, [trades, selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag]);

  const data = {
    datasets: [
      {
        label: 'Wins',
        data: calculateRRData.filter((d) => d.outcome === 'Win'),
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        pointRadius: 6,
        pointHoverRadius: 8,
      },
      {
        label: 'Losses',
        data: calculateRRData.filter((d) => d.outcome === 'Loss'),
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        title: { display: true, text: 'Returns ($)' },
        type: 'linear',
        grid: { display: true },
      },
      y: {
        title: { display: true, text: 'Risk-Reward Ratio' },
        type: 'linear',
        grid: { display: true },
        ticks: { stepSize: 0.5 },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => `Return: $${context.raw.x.toFixed(2)}, RR: ${context.raw.y.toFixed(2)}`,
        },
      },
    },
  };

  const tooltipContent = `This chart shows how your risk-reward (RR) ratios correlate with trade outcomes for ${accountName}. Each point represents a trade, with the Y-axis showing the RR ratio (TP/SL) and the X-axis showing returns. Green points are wins, red are losses. Filter by period and tags to analyze specific strategies.`;

  const renderContent = () => {
    if (loading) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </MDBox>
      );
    }
    if (!hasData) {
      return (
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
          textAlign="center"
          px={2}
        >
          <InfoIcon sx={{ fontSize: 40, mb: 2, color: iconColor }} />
          <MDTypography variant="h6" color="text">
            No trading data available
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Complete some trades to see your RR ratio analysis
          </MDTypography>
        </MDBox>
      );
    }
    return (
      <MDBox height="100%">
        <Scatter data={data} options={chartOptions} />
      </MDBox>
    );
  };

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <TimelineIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Risk-Reward Ratio Analysis {tradeAccountID === 'default' ? '' : `(${accountName})`} {showMissedTrades ? '(Missed)' : ''}
            </MDTypography>
            <Tooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                  },
                },
              }}
            >
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </Tooltip>
          </MDBox>
        </MDBox>
        <Divider />
        {renderContent()}
      </MDBox>
    </Card>
  );
};

TeamRRRatioAnalysisChart.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string,
};

export default TeamRRRatioAnalysisChart;